import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CodeBox } from './CodeBox';

/**
 * The owner code, shown ONCE (AUT-403), after the `?state=created` modal of
 * `companies-create.html`. The plaintext lives only in the parent's
 * component state (never the query cache, never storage) and the parent
 * drops it on close — `code === null` means closed. «Перевыпустить»
 * replaces it (the server revokes the unused previous one). Closing needs
 * the explicit «Я сохранил код»; a backdrop click or Esc does nothing.
 */
export interface OwnerCodeDialogProps {
  code: string | null;
  companyName: string;
  /** `created` — right after create; `issued` — a re-issue from the card. */
  variant: 'created' | 'issued';
  reissuing: boolean;
  onReissue: () => void;
  onClose: () => void;
}

export const OwnerCodeDialog = ({
  code,
  companyName,
  variant,
  reissuing,
  onReissue,
  onClose,
}: OwnerCodeDialogProps): ReactElement => {
  const { t } = useTranslation();
  const ns = 'companies.ownerCode';
  return (
    <Dialog open={code !== null} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        {t(`${ns}.title.${variant}`)}
        <Typography variant="body2" color="text.secondary">
          {t(`${ns}.subtitle`, { name: companyName })}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CodeBox label={t(`${ns}.label`)} code={code ?? ''} testId="company-owner-code-value" />
        <Alert severity="warning">
          <b>{t(`${ns}.onceTitle`)}</b>
          <br />
          {t(`${ns}.onceBody`)}
        </Alert>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            {t(`${ns}.stepsTitle`)}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {(['step1', 'step2', 'step3'] as const).map((step) => (
              <Typography key={step} component="li" variant="body2">
                {t(`${ns}.${step}`)}
              </Typography>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button onClick={onReissue} disabled={reissuing} loading={reissuing}>
            {t(`${ns}.reissue`)}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t(`${ns}.reissueHint`)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={onClose}
          disabled={reissuing}
          data-testid="company-owner-code-done"
        >
          {t(`${ns}.done`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
